// src/modules/competitions/components/ImageTypeDetailModal.tsx

import React, { useState, useEffect } from 'react';
import Button from '../../../shared/components/ui/Button';
import Loading from '../../../shared/components/Loading';
import ImageComparator from './ImageComparator';
import CompactUploader from './CompactUploader';
import { CompetitionImageType, CompetitionSubmission, ExampleImage } from '../types';
import { imageTypesService } from '../services/imageTypesService';
import '../styles/imageTypeModal.css';

interface Props {
    imageType: CompetitionImageType;
    participantId?: number;
    existingSubmission?: CompetitionSubmission;
    categoryRules?: string | null;
    onClose: () => void;
    onUploadSuccess: () => void;
}

type TabType = 'image' | 'details' | 'criteria' | 'rules';

const ImageTypeDetailModal: React.FC<Props> = ({
    imageType,
    participantId,
    existingSubmission,
    categoryRules,
    onClose,
    onUploadSuccess
}) => {
    const [activeTab, setActiveTab] = useState<TabType>('image');
    const [examples, setExamples] = useState<ExampleImage[]>([]);
    const [currentExampleIndex, setCurrentExampleIndex] = useState(0);
    const [criteria, setCriteria] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isUploading, setIsUploading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        loadData();
    }, [imageType.id]);

    useEffect(() => {
        return () => {
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    const loadData = async () => {
        try {
            setIsLoading(true);

            // Cargar ejemplos
            const examplesRes = await imageTypesService.getExampleImages(imageType.id);
            if (examplesRes.success && examplesRes.data) {
                setExamples(examplesRes.data.examples);
            }

            // Cargar criterios
            const criteriaRes = await imageTypesService.getCriteriaByImageType(imageType.id);
            if (criteriaRes.success && criteriaRes.data) {
                setCriteria(criteriaRes.data.criteria);
            }
        } catch (err) {
            console.error('Error loading modal data:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFileSelect = (file: File, url: string) => {
        setSelectedFile(file);
        setPreviewUrl(url);
        setError(null);
    };

    const handleUpload = async () => {
        if (!selectedFile || !participantId) return;

        try {
            setIsUploading(true);
            setError(null);

            const formData = new FormData();
            formData.append('image', selectedFile);
            formData.append('participant_id', participantId.toString());
            formData.append('image_type_id', imageType.id.toString());

            const response = await imageTypesService.submitImage(formData);

            if (response.success) {
                onUploadSuccess();
                onClose();
            } else {
                setError(response.message || 'Error al subir imagen');
            }
        } catch (err) {
            setError('Error al subir imagen');
        } finally {
            setIsUploading(false);
        }
    };

    const handleCancelUpload = () => {
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
        }
        setSelectedFile(null);
        setPreviewUrl(null);
        setError(null);
    };

    const handleDelete = async () => {
        if (!existingSubmission || !participantId) return;

        const confirmed = window.confirm('¿Estás seguro de eliminar esta imagen?');
        if (!confirmed) return;

        try {
            setIsDeleting(true);
            setError(null);

            const response = await imageTypesService.deleteSubmission(
                existingSubmission.id,
                participantId
            );

            if (response.success) {
                onUploadSuccess();
                onClose();
            } else {
                setError(response.message || 'Error al eliminar imagen');
            }
        } catch (err) {
            setError('Error al eliminar imagen');
        } finally {
            setIsDeleting(false);
        }
    };

    const nextExample = () => {
        setCurrentExampleIndex((prev) => (prev + 1) % examples.length);
    };

    const prevExample = () => {
        setCurrentExampleIndex((prev) => (prev - 1 + examples.length) % examples.length);
    };

    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [onClose]);

    const renderImageTab = () => {
        const exampleImage = examples[currentExampleIndex];
        const hasSubmission = !!existingSubmission;
        const isUploadingNew = !!selectedFile;

        return (
            <div className="tab-content-image">
                {/* Comparador o Vista Simple */}
                {hasSubmission && !isUploadingNew ? (
                    <>
                        <ImageComparator
                            exampleImage={exampleImage.image_url}
                            participantImage={`/api/api/v1/competitions/get-image.php?id=${existingSubmission.id}`}
                        />
                        {participantId && (
                            <div className="image-actions">
                                <Button
                                    onClick={handleDelete}
                                    variant="danger"
                                    disabled={isDeleting}
                                    className="btn-delete"
                                >
                                    {isDeleting ? 'Eliminando...' : '🗑️ Eliminar y subir nueva'}
                                </Button>
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        {/* Mostrar solo ejemplo si no hay imagen del participante */}
                        <div className="single-image-view">
                            <div className="example-image-container">
                                <span className="image-label">EJEMPLO</span>
                                <img
                                    src={exampleImage.image_url}
                                    alt={exampleImage.title || 'Ejemplo'}
                                    className="single-image"
                                />
                            </div>
                            {exampleImage.title && (
                                <h4 className="example-title">{exampleImage.title}</h4>
                            )}
                            {exampleImage.description && (
                                <p className="example-description">{exampleImage.description}</p>
                            )}
                        </div>

                        {/* Navegación de ejemplos */}
                        {examples.length > 1 && (
                            <div className="example-navigation">
                                <Button onClick={prevExample} variant="secondary" className="btn-nav">
                                    ← Anterior
                                </Button>
                                <span className="example-counter">
                                    {currentExampleIndex + 1} / {examples.length}
                                </span>
                                <Button onClick={nextExample} variant="secondary" className="btn-nav">
                                    Siguiente →
                                </Button>
                            </div>
                        )}

                        {/* Uploader compacto */}
                        {participantId && (
                            <CompactUploader
                                imageType={imageType}
                                onFileSelect={handleFileSelect}
                                onUpload={handleUpload}
                                onCancel={handleCancelUpload}
                                isUploading={isUploading}
                                selectedFile={selectedFile}
                                previewUrl={previewUrl}
                                error={error}
                            />
                        )}
                    </>
                )}
            </div>
        );
    };

    const renderDetailsTab = () => {
        return (
            <div className="tab-content-details">
                {imageType.description && (
                    <div className="details-section">
                        <h3>📝 Descripción</h3>
                        <p>{imageType.description}</p>
                    </div>
                )}

                {imageType.instructions && (
                    <div className="details-section">
                        <h3>📋 Instrucciones</h3>
                        <div className="instructions-text">{imageType.instructions}</div>
                    </div>
                )}

                <div className="details-section">
                    <h3>⚙️ Especificaciones Técnicas</h3>
                    <div className="specs-list">
                        <div className="spec-item">
                            <span className="spec-label">Formatos permitidos:</span>
                            <span className="spec-value">
                                {imageType.allowed_formats.map(f => f.toUpperCase()).join(', ')}
                            </span>
                        </div>
                        <div className="spec-item">
                            <span className="spec-label">Tamaño máximo:</span>
                            <span className="spec-value">{imageType.max_file_size_mb} MB</span>
                        </div>
                        <div className="spec-item">
                            <span className="spec-label">Obligatoria:</span>
                            <span className={`spec-value ${imageType.is_required ? 'required' : 'optional'}`}>
                                {imageType.is_required ? '✓ Sí' : '✗ No'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderCriteriaTab = () => {
        if (criteria.length === 0) {
            return (
                <div className="tab-content-empty">
                    <p>No hay criterios de evaluación definidos para esta imagen.</p>
                </div>
            );
        }

        return (
            <div className="tab-content-criteria">
                <div className="criteria-list">
                    {criteria.map((criterion) => (
                        <div key={criterion.id} className="criterion-card">
                            <div className="criterion-header">
                                <h4>{criterion.name}</h4>
                                <span className="criterion-weight">Peso: {criterion.weight}x</span>
                            </div>
                            {criterion.description && (
                                <p className="criterion-description">{criterion.description}</p>
                            )}
                            <div className="criterion-score">
                                <span>Puntuación: {criterion.min_score} - {criterion.max_score}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderRulesTab = () => {
        if (!categoryRules) {
            return (
                <div className="tab-content-empty">
                    <p>No hay reglamento disponible para esta categoría.</p>
                </div>
            );
        }

        return (
            <div className="tab-content-rules">
                <div className="rules-content">
                    <div dangerouslySetInnerHTML={{ __html: categoryRules }} />
                </div>
            </div>
        );
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-container modal-tabs" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="modal-header">
                    <h2>{imageType.name}</h2>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>

                {/* Tabs Navigation */}
                <div className="tabs-navigation">
                    <button
                        className={`tab-button ${activeTab === 'image' ? 'active' : ''}`}
                        onClick={() => setActiveTab('image')}
                    >
                        📸 Imagen
                    </button>
                    <button
                        className={`tab-button ${activeTab === 'details' ? 'active' : ''}`}
                        onClick={() => setActiveTab('details')}
                    >
                        📋 Detalles
                    </button>
                    <button
                        className={`tab-button ${activeTab === 'criteria' ? 'active' : ''}`}
                        onClick={() => setActiveTab('criteria')}
                    >
                        ⭐ Criterios
                    </button>
                    {categoryRules && (
                        <button
                            className={`tab-button ${activeTab === 'rules' ? 'active' : ''}`}
                            onClick={() => setActiveTab('rules')}
                        >
                            📜 Reglamento
                        </button>
                    )}
                </div>

                {/* Body */}
                <div className="modal-body">
                    {isLoading ? (
                        <Loading message="Cargando información..." />
                    ) : (
                        <>
                            {activeTab === 'image' && renderImageTab()}
                            {activeTab === 'details' && renderDetailsTab()}
                            {activeTab === 'criteria' && renderCriteriaTab()}
                            {activeTab === 'rules' && renderRulesTab()}
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="modal-footer">
                    <Button onClick={onClose} variant="secondary">
                        Cerrar
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default ImageTypeDetailModal;